import { DEFAULT_CURRENCY } from './constants';

/**
 * Extract brand from product
 * @param {Object} product - Product object
 * @returns {string} Brand name
 */
function getBrandFromProduct(product) {
  if (!product) return '';

  const brandField = product?.customFields?.find(
    field => field.key === 'ProductBrand'
  );
  return (
    brandField?.stringValue ||
    brandField?.value ||
    product.brand?.name ||
    product.subName ||
    ''
  );
}

/**
 * Capitalize first letter of a string
 * @param {string} str - String to capitalize
 * @returns {string} Capitalized string
 */
function capitalizeFirstLetter(str) {
  if (!str || typeof str !== 'string') return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Extract category hierarchy from canonicalCategory (nested parent structure)
 * @param {Object} canonicalCategory - Product's canonical category with nested parents
 * @returns {Array} Array of category names from root to leaf
 */
function getCategoryHierarchyFromCanonical(canonicalCategory) {
  const hierarchy = [];
  let current = canonicalCategory;

  // Build hierarchy from leaf to root
  while (current && current.name) {
    hierarchy.unshift(current.name); // Add to beginning to reverse order
    current = current.parent;
  }

  return hierarchy;
}

/**
 * Extract category hierarchy from product or provided route parents
 * @param {Object} product - Product object
 * @param {Array} routeParents - Optional route parents array (from category page context)
 * @returns {Object} Category object with item_category fields
 */
function getCategoriesFromProduct(product, routeParents = null) {
  const categories = {};

  // Safety check
  if (!product) return categories;

  // First priority: Use canonicalCategory with nested parents (proper names)
  if (product.canonicalCategory) {
    const hierarchy = getCategoryHierarchyFromCanonical(
      product.canonicalCategory
    );
    hierarchy.forEach((name, index) => {
      if (index === 0) {
        categories.item_category = name;
      } else if (index === 1) {
        categories.item_category2 = name;
      } else if (index === 2) {
        categories.item_category3 = name;
      } else if (index === 3) {
        categories.item_category4 = name;
      }
    });
    if (categories.item_category) {
      return categories;
    }
  }

  // Second priority: Use provided route parents (from category page context)
  // These have proper breadcrumb names like "Häst" instead of slugs like "hast"
  if (routeParents && Array.isArray(routeParents) && routeParents.length > 0) {
    routeParents.forEach((parent, index) => {
      const categoryName = parent?.object?.breadcrumbText || '';
      if (categoryName) {
        if (index === 0) {
          categories.item_category = categoryName;
        } else if (index === 1) {
          categories.item_category2 = categoryName;
        } else if (index === 2) {
          categories.item_category3 = categoryName;
        } else if (index === 3) {
          categories.item_category4 = categoryName;
        }
      }
    });
    if (categories.item_category) {
      return categories;
    }
  }

  // Third priority: Try to get from product.parents (these are already properly capitalized)
  // NOTE: parents array is in reverse order (most specific first), so we reverse it
  if (
    product.parents &&
    Array.isArray(product.parents) &&
    product.parents.length > 0
  ) {
    const reversedParents = [...product.parents].reverse();
    reversedParents.forEach((parent, index) => {
      const categoryName =
        parent?.object?.breadcrumbText || parent?.object?.name || '';
      if (categoryName) {
        if (index === 0) {
          categories.item_category = categoryName;
        } else if (index === 1) {
          categories.item_category2 = categoryName;
        } else if (index === 2) {
          categories.item_category3 = categoryName;
        } else if (index === 3) {
          categories.item_category4 = categoryName;
        }
      }
    });
  }

  // Fallback: Try to get from primaryRoute.path (which is a string like "/category/subcategory")
  // These need to be capitalized to match the format from parents
  if (
    !categories.item_category &&
    product.primaryRoute?.path &&
    typeof product.primaryRoute.path === 'string'
  ) {
    const pathSegments = product.primaryRoute.path
      .split('/')
      .filter(segment => segment && segment.trim())
      .slice(0, 5); // Max 5 levels

    pathSegments.forEach((segment, index) => {
      // Capitalize first letter to match parent breadcrumb format
      const capitalizedSegment = capitalizeFirstLetter(segment);

      if (index === 0) {
        categories.item_category = capitalizedSegment;
      } else if (index === 1) {
        categories.item_category2 = capitalizedSegment;
      } else if (index === 2) {
        categories.item_category3 = capitalizedSegment;
      } else if (index === 3) {
        categories.item_category4 = capitalizedSegment;
      }
    });
  }

  return categories;
}

/**
 * Format a product for GA4 tracking
 * @param {Object} product - Product object from GraphQL
 * @param {Object} options - Additional options
 * @param {string} options.listName - Name of the list (for list tracking)
 * @param {number} options.index - Position in list
 * @param {Object} options.variant - Selected variant
 * @param {number} options.quantity - Quantity
 * @param {Array} options.routeParents - Route parents for category hierarchy (from page context)
 * @returns {Object} Formatted product for GA4
 */
export function formatProduct(product, options = {}) {
  if (!product) return null;

  const { listName, index, variant, quantity = 1, routeParents } = options;

  // Use variant data if available, otherwise use product data
  const activeVariant = variant || product.variant || null;
  const articleNumber = activeVariant?.articleNumber || product.articleNumber;
  const name = product.name;
  const price = activeVariant?.price?.incVat || product.price?.incVat || 0;
  const previousPrice =
    activeVariant?.previousPrice?.incVat || product.previousPrice?.incVat || 0;

  // Get category from parents/breadcrumbs (use routeParents if provided for proper names)
  const categories = getCategoriesFromProduct(product, routeParents);

  // Get brand from customFields (same logic as ProductPage.js getBrand)
  const brand = getBrandFromProduct(product);

  // Determine sales type
  let salesType = 'Full price';
  const hasDiscount = previousPrice > 0 && previousPrice > price;
  const badges = Array.isArray(product?.badges) ? product.badges : [];

  if (hasDiscount) {
    // Check if it's a member deal
    const hasMemberPrice = product?.hasMemberPrice === true;
    const isMemberBadge = badges.some(
      badge =>
        badge?.text?.toLowerCase().includes('member') ||
        badge?.text?.toLowerCase().includes('medlem')
    );

    if (hasMemberPrice || isMemberBadge) {
      salesType = 'Member deals';
    } else {
      // Check if it's a campaign or sale
      const isCampaignBadge = badges.some(
        badge =>
          badge?.text?.toLowerCase().includes('campaign') ||
          badge?.text?.toLowerCase().includes('kampanj')
      );
      const isSaleBadge = badges.some(
        badge =>
          badge?.text?.toLowerCase().includes('sale') ||
          badge?.text?.toLowerCase().includes('rea')
      );

      salesType = isCampaignBadge ? 'Campaign' : isSaleBadge ? 'Sale' : 'Sale';
    }
  }

  // Calculate discount
  const discount = hasDiscount ? previousPrice - price : undefined;

  // Extract sizes from variant values (e.g., ["39"] or ["M", "Blue"])
  let sizes = '';
  let sizeValue = '';
  if (activeVariant?.values && Array.isArray(activeVariant.values)) {
    sizes = activeVariant.values.join(', ');
    // Use the first value as the primary size/variant identifier
    sizeValue = activeVariant.values[0] || '';
  }

  // item_variant should be the product color (from subName)
  const itemVariant = product.subName || '';

  // Build the item object
  const item = {
    item_id: articleNumber,
    item_name: name,
    price: price,
    quantity: quantity,
    item_variant: itemVariant,
    discount: discount,
    sales_type: salesType,
    sizes: sizes || undefined
  };

  // Add optional fields
  if (categories.item_category) {
    item.item_category = categories.item_category;
  }
  if (categories.item_category2) {
    item.item_category2 = categories.item_category2;
  }
  if (categories.item_category3) {
    item.item_category3 = categories.item_category3;
  }
  if (categories.item_category4) {
    item.item_category4 = categories.item_category4;
  }
  if (brand) {
    item.item_brand = brand;
  }
  if (listName) {
    item.item_list_name = listName;
    item.item_list_id = listName.toLowerCase().replace(/\s+/g, '_');
  }
  if (typeof index === 'number') {
    item.index = index;
  }

  return item;
}

/**
 * Format cart item for GA4 tracking
 * @param {Object} cartItem - Cart item from GraphQL
 * @param {number} index - Position in cart
 * @returns {Object} Formatted item for GA4
 */
export function formatCartItem(cartItem, index = 0) {
  if (!cartItem) return null;

  const product = cartItem.product;
  const brand = getBrandFromProduct(product);
  const categories = getCategoriesFromProduct(product);

  // Calculate discount if applicable
  const discount = cartItem.discount?.incVat || 0;

  // Get coupon from cart item discounts
  const coupon = cartItem.discounts?.[0]?.name || '';

  // Use variant article number if available (to match formatProduct behavior)
  const itemId = cartItem.variant?.articleNumber || cartItem.articleNumber;

  // item_variant should be the product color (from subName)
  const itemVariant = cartItem.product?.subName || '';

  return {
    item_id: itemId,
    item_name: product?.name || cartItem.articleNumber,
    price: cartItem.unitPrice?.incVat || 0,
    quantity: cartItem.quantity,
    item_brand: brand,
    item_variant: itemVariant,
    index: index,
    discount: discount,
    ...(coupon && { coupon }),
    ...categories
  };
}

/**
 * Format cart item for mini cart (with extended fields)
 * @param {Object} cartItem - Cart item from GraphQL
 * @param {number} index - Position in cart
 * @param {string} currency - Currency code
 * @returns {Object} Formatted item for mini cart tracking
 */
export function formatMiniCartItem(
  cartItem,
  index = 0,
  currency = DEFAULT_CURRENCY
) {
  if (!cartItem) return null;

  const product = cartItem.product;
  const brand = getBrandFromProduct(product);
  const categories = getCategoriesFromProduct(product);

  const unitPrice = cartItem.unitPrice?.incVat || 0;
  const cartItemPreviousPrice = cartItem.previousUnitPrice?.incVat || 0;
  const discount = cartItem.discount?.incVat || 0;

  // Get the actual original price from variant or product level
  // (sometimes cartItem.previousUnitPrice doesn't reflect the original price)
  const variantPreviousPrice = cartItem.variant?.previousPrice?.incVat || 0;
  const productPreviousPrice = product?.previousPrice?.incVat || 0;
  const actualPreviousPrice =
    variantPreviousPrice || productPreviousPrice || cartItemPreviousPrice;

  // Determine sales type
  let salesType = 'Full price';

  // Check if there's a discount (check multiple sources)
  const hasDiscount =
    discount > 0 ||
    (cartItemPreviousPrice > 0 && cartItemPreviousPrice > unitPrice) ||
    (actualPreviousPrice > 0 && actualPreviousPrice > unitPrice);

  if (hasDiscount) {
    // Check if it's a member deal
    const hasMemberPrice = product?.hasMemberPrice === true;
    const hasMemberDiscount = cartItem.discounts?.some(
      d =>
        d.name?.toLowerCase().includes('member') ||
        d.name?.toLowerCase().includes('medlem')
    );

    if (hasMemberPrice || hasMemberDiscount) {
      salesType = 'Member deals';
    } else {
      // Check if it's explicitly a campaign/sale
      const hasCampaign = cartItem.discounts?.some(
        d =>
          d.campaign?.name ||
          d.name?.toLowerCase().includes('campaign') ||
          d.name?.toLowerCase().includes('kampanj') ||
          d.name?.toLowerCase().includes('sale') ||
          d.name?.toLowerCase().includes('rea')
      );
      salesType = hasCampaign ? 'Campaign' : 'Sale';
    }
  }

  // Extract size from variant values or variant article number
  let sizes = '';
  if (cartItem.variant?.values && Array.isArray(cartItem.variant.values)) {
    // Try to find size in variant values
    sizes = cartItem.variant.values.join(', ');
  } else if (
    cartItem.variantOptionNames &&
    cartItem.variantOptionNames.length > 0
  ) {
    // Fallback to variant option names
    sizes = cartItem.variantOptionNames.join(', ');
  }

  // Calculate actual discount amount
  const actualDiscount =
    discount > 0
      ? discount
      : actualPreviousPrice > unitPrice
      ? actualPreviousPrice - unitPrice
      : 0;

  // Use variant article number if available (to match formatProduct behavior)
  const itemId = cartItem.variant?.articleNumber || cartItem.articleNumber;

  // item_variant should be the product color (from subName)
  const itemVariant = product?.subName || '';

  return {
    item_id: itemId,
    item_name: product?.name || cartItem.articleNumber,
    currency: currency,
    item_brand: brand || '',
    ...categories,
    item_variant: itemVariant,
    price: unitPrice,
    index: index,
    discount: actualDiscount > 0 ? actualDiscount : undefined,
    sales_type: salesType,
    quantity: cartItem.quantity,
    sizes: sizes || undefined
  };
}

/**
 * Format multiple products for GA4
 * @param {Array} products - Array of products
 * @param {Object} options - Formatting options
 * @param {Array} options.routeParents - Route parents for category hierarchy
 * @returns {Array} Formatted products
 */
export function formatProducts(products, options = {}) {
  if (!Array.isArray(products)) return [];

  return products
    .map((product, index) =>
      formatProduct(product, {
        ...options,
        index: options.startIndex ? options.startIndex + index : index,
        routeParents: options.routeParents
      })
    )
    .filter(Boolean);
}

/**
 * Format cart for GA4
 * @param {Object} cart - Cart object from GraphQL
 * @returns {Object} Formatted cart data
 */
export function formatCart(cart) {
  if (!cart || !cart.items) return null;

  const items = cart.items
    .map((item, index) => formatCartItem(item, index))
    .filter(Boolean);

  return {
    currency: cart.currency?.code || DEFAULT_CURRENCY,
    value: cart.productTotal?.incVat || 0,
    items: items
  };
}

/**
 * Get currency code from channel or cart
 * @param {Object} options - Options object
 * @param {Object} options.channel - Channel object
 * @param {Object} options.cart - Cart object
 * @returns {string} Currency code
 */
export function getCurrency({ channel, cart } = {}) {
  if (cart?.currency?.code) {
    return cart.currency.code;
  }
  if (channel?.defaultCurrency?.code) {
    return channel.defaultCurrency.code;
  }
  return DEFAULT_CURRENCY;
}

/**
 * Calculate total value from products
 * @param {Array} items - Array of formatted items
 * @returns {number} Total value
 */
export function calculateValue(items) {
  if (!Array.isArray(items)) return 0;

  return items.reduce((total, item) => {
    const price = item.price || 0;
    const quantity = item.quantity || 1;
    return total + price * quantity;
  }, 0);
}
